// ============================================
// STATE - UI Store (estado global de UI)
// ============================================

import { create } from 'zustand';

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Modales
  modalStack: string[];
  openModal: (modalId: string) => void;
  closeModal: (modalId: string) => void;
  closeAllModals: () => void;
  isModalOpen: (modalId: string) => boolean;

  // Toast/Notificaciones (simplificado, puedes usar sonner/react-hot-toast)
  toastQueue: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
  }>;
  addToast: (
    type: 'success' | 'error' | 'warning' | 'info',
    message: string
  ) => void;
  removeToast: (id: string) => void;

  // Estado de red (para offline)
  isOnline: boolean;
  setIsOnline: (online: boolean) => void;

  // Loading global
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  sidebarOpen: true,

  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  modalStack: [],

  openModal: (modalId) => {
    const stack = get().modalStack;
    if (!stack.includes(modalId)) {
      set({ modalStack: [...stack, modalId] });
    }
  },

  closeModal: (modalId) => {
    set({ modalStack: get().modalStack.filter((id) => id !== modalId) });
  },

  closeAllModals: () => set({ modalStack: [] }),

  isModalOpen: (modalId) => get().modalStack.includes(modalId),

  toastQueue: [],

  addToast: (type, message) => {
    const id = crypto.randomUUID();
    set({
      toastQueue: [...get().toastQueue, { id, type, message }],
    });
    // Auto-remove después de 5s
    setTimeout(() => {
      get().removeToast(id);
    }, 5000);
  },

  removeToast: (id) => {
    set({ toastQueue: get().toastQueue.filter((t) => t.id !== id) });
  },

  isOnline: navigator.onLine,

  setIsOnline: (online) => set({ isOnline: online }),

  isLoading: false,

  setIsLoading: (loading) => set({ isLoading: loading }),
}));

// Listener de conectividad
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useUIStore.getState().setIsOnline(true);
  });

  window.addEventListener('offline', () => {
    useUIStore.getState().setIsOnline(false);
  });
}
